// src/pages/suppliers/SupplierDashboard.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  CardActions,
  CircularProgress,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
  Popover,
  AlertTitle,
  Badge,
  Stack,
  Paper as MuiPaper,
  Tab,
  Tabs,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  StarOutline as StarOutlineIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';

// ============================================
// CHART IMPORTS
// ============================================
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
  AreaChart,
} from 'recharts';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS, StatusChip } from '../buyers/components/styles';
import { formatCurrency, formatDate } from '../buyers/components/utils';
import DocumentUpload from '../suppliers/DocumentUpload';

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[SUPPLIER-DASH] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[SUPPLIER-DASH] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[SUPPLIER-DASH] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[SUPPLIER-DASH] ⚠️ ${message}`, data || ''),
};

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  PRODUCTS: {
    LIST: `${API_BASE_URL}/products/register.php`,
  },
  ORDERS: {
    LIST: `${API_BASE_URL}/orders/orders.php`,
    UPDATE: (id) => `${API_BASE_URL}/orders/orders.php?id=${id}`,
  },
  SUPPLIER: {
    PROFILE: `${API_BASE_URL}/suppliers/profile.php`,
    STATS: `${API_BASE_URL}/suppliers/stats.php`,
    ANALYTICS: `${API_BASE_URL}/suppliers/analytics.php`,
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const getStatusColor = (status) => {
  const colors = {
    'active': BRAND_COLORS.success,
    'inactive': BRAND_COLORS.error,
    'pending': BRAND_COLORS.warning,
    'draft': BRAND_COLORS.info,
    'processing': BRAND_COLORS.info,
    'shipped': BRAND_COLORS.primary,
    'delivered': BRAND_COLORS.success,
    'cancelled': BRAND_COLORS.error,
    'out_of_stock': '#FF6B6B',
  };
  return colors[status] || BRAND_COLORS.info;
};

const getStatusLabel = (status) => {
  const labels = {
    'active': 'Active',
    'inactive': 'Inactive',
    'pending': 'Pending',
    'draft': 'Draft',
    'processing': 'Processing',
    'shipped': 'Shipped',
    'delivered': 'Delivered',
    'cancelled': 'Cancelled',
    'out_of_stock': 'Out of Stock',
  };
  return labels[status] || status || 'N/A';
};

const getInitials = (name) => {
  if (!name) return 'S';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return parts[0][0] + parts[1][0];
  }
  return name.substring(0, 2).toUpperCase();
};

// ============================================
// CUSTOM COMPONENTS
// ============================================

const GlossyGradientCard = ({ icon, title, value, subtitle, color, trend, trendValue, progress, children, ...props }) => {
  const theme = useTheme();
  
  return (
    <Zoom in style={{ transitionDelay: '100ms' }}>
      <Card
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          background: `linear-gradient(135deg, ${color}20 0%, ${color}05 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(color, 0.15)}`,
          boxShadow: `0 8px 32px ${alpha(color, 0.1)}`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 30% 30%, ${alpha(color, 0.1)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          },
          '&:hover': {
            transform: 'translateY(-6px) scale(1.01)',
            boxShadow: `0 16px 48px ${alpha(color, 0.2)}`,
            borderColor: alpha(color, 0.3),
          },
        }}
      >
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600, 
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  mt: 0.5, 
                  color: color,
                  background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.6)})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    display: 'block', 
                    mt: 0.5,
                    fontWeight: 500,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {trend === 'up' ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14, color: BRAND_COLORS.success }} />
                  ) : trend === 'down' ? (
                    <ArrowDownwardIcon sx={{ fontSize: 14, color: BRAND_COLORS.error }} />
                  ) : null}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: trend === 'up' ? BRAND_COLORS.success : trend === 'down' ? BRAND_COLORS.error : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {trendValue}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${alpha(color, 0.2)}, transparent 70%)`,
                  animation: 'pulse 2s ease-in-out infinite',
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(color, 0.15),
                  color: color,
                  width: 56,
                  height: 56,
                  border: `2px solid ${alpha(color, 0.2)}`,
                  '& .MuiSvgIcon-root': { fontSize: 28 },
                }}
              >
                {icon}
              </Avatar>
            </Box>
          </Box>
          {progress !== undefined && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                mt: 2,
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(color, 0.1),
                '& .MuiLinearProgress-bar': {
                  bgcolor: color,
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`,
                },
              }}
            />
          )}
          {children}
        </CardContent>
      </Card>
    </Zoom>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const SupplierDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'supplier';
  const accessToken = localStorage.getItem('access_token');

  // ============================================
  // STATE
  // ============================================

  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('table');

  // Document upload state - changed to dialog mode
  const [documentUploadDialogOpen, setDocumentUploadDialogOpen] = useState(false);
  const [documentUploadComplete, setDocumentUploadComplete] = useState(false);
  const [documentUploadCount, setDocumentUploadCount] = useState(0);

  // Data states
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [supplierData, setSupplierData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    averageRating: 0,
    totalStock: 0,
  });

  // Chart data states (from live data)
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [growthData, setGrowthData] = useState({
    revenue_growth: 0,
    orders_growth: 0,
    current_revenue: 0,
    previous_revenue: 0,
  });

  // UI states
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Dialog states
  const [viewProductDialog, setViewProductDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);

  // ============================================
  // API FUNCTIONS
  // ============================================

  const fetchAnalytics = useCallback(async () => {
    logger.info('Fetching analytics data...');
    
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.SUPPLIER.ANALYTICS}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success && result.data) {
        const data = result.data;
        
        // Update stats
        if (data.stats) {
          setStats(prev => ({
            ...prev,
            totalProducts: data.stats.totalProducts || 0,
            activeProducts: data.stats.activeProducts || 0,
            totalOrders: data.stats.totalOrders || 0,
            pendingOrders: (data.stats.pendingOrders || 0) + (data.stats.processingOrders || 0),
            totalRevenue: data.stats.totalRevenue || 0,
            averageRating: data.stats.averageRating || 0,
            totalStock: data.stats.totalStock || 0,
          }));
        }
        
        // Update chart data
        if (data.charts) {
          setMonthlyRevenue(data.charts.monthlyRevenue || []);
          setCategoryDistribution(data.charts.categoryDistribution || []);
          setOrderStatusData(data.charts.orderStatus || []);
        }
        
        // Update top products
        if (data.topProducts) {
          setTopProducts(data.topProducts);
        }
        
        // Update growth data
        if (data.growth) {
          setGrowthData(data.growth);
        }
        
        setAnalyticsData(data);
        logger.info('✅ Analytics data loaded successfully');
      } else {
        logger.warn('Analytics API returned error:', result.message);
        // Fallback to mock data if API fails
        generateFallbackData();
      }
    } catch (error) {
      logger.error('Error fetching analytics:', error);
      // Generate fallback data on error
      generateFallbackData();
    }
  }, []);

  const generateFallbackData = useCallback(() => {
    logger.info('Generating fallback data...');
    
    // Generate monthly revenue fallback
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    setMonthlyRevenue(months.map(m => ({
      month: m,
      revenue: Math.floor(Math.random() * 200000) + 50000,
      orders: Math.floor(Math.random() * 15) + 3,
    })));
    
    // Category distribution fallback
    const categories = ['Solvents', 'Acids', 'Bases', 'Salts', 'Oxides', 'Polymers'];
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#FF8A5C'];
    setCategoryDistribution(categories.map((cat, i) => ({
      name: cat,
      value: Math.floor(Math.random() * 30) + 5,
      color: colors[i % colors.length],
    })));
    
    // Order status fallback
    const statuses = ['Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];
    const statusColors = ['#FF9800', '#2196F3', '#9C27B0', '#4CAF50', '#F44336'];
    setOrderStatusData(statuses.map((s, i) => ({
      name: s,
      value: Math.floor(Math.random() * 10) + 1,
      color: statusColors[i],
    })));
  }, []);

  const fetchSupplierData = useCallback(async () => {
    logger.info('Fetching supplier profile...');
    
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.SUPPLIER.PROFILE}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setSupplierData(result.data);
        logger.info('Supplier profile loaded successfully');
      } else {
        logger.warn('Profile API returned error:', result.message);
      }
    } catch (error) {
      logger.error('Error fetching supplier data:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    logger.info('Fetching products...');

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.PRODUCTS.LIST}?supplier_id=${userId}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        let productsData = [];

        if (Array.isArray(result.data)) {
          productsData = result.data;
        } else if (result.data && typeof result.data === 'object') {
          if (Array.isArray(result.data.data)) {
            productsData = result.data.data;
          } else if (Array.isArray(result.data.products)) {
            productsData = result.data.products;
          } else {
            const arrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
            if (arrayKey) {
              productsData = result.data[arrayKey];
            }
          }
        } else if (Array.isArray(result)) {
          productsData = result;
        }

        setProducts(productsData);
        logger.info(`✅ Loaded ${productsData.length} products`);
      } else {
        throw new Error(result.message || 'Failed to fetch products');
      }
    } catch (error) {
      logger.error('Error fetching products:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load products. Please try again.',
        severity: 'error',
      });
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    logger.info('Fetching orders...');

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.ORDERS.LIST}?supplier_id=${userId}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        let ordersData = [];

        if (Array.isArray(result.data)) {
          ordersData = result.data;
        } else if (result.data && typeof result.data === 'object') {
          if (Array.isArray(result.data.data)) {
            ordersData = result.data.data;
          } else if (Array.isArray(result.data.orders)) {
            ordersData = result.data.orders;
          } else {
            const arrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
            if (arrayKey) {
              ordersData = result.data[arrayKey];
            }
          }
        } else if (Array.isArray(result)) {
          ordersData = result;
        }

        setOrders(ordersData);
        logger.info(`✅ Loaded ${ordersData.length} orders`);
      } else {
        throw new Error(result.message || 'Failed to fetch orders');
      }
    } catch (error) {
      logger.error('Error fetching orders:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load orders. Please try again.',
        severity: 'error',
      });
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    logger.info('Loading dashboard data...');
    setLoading(true);
    setLoadingError(null);
    
    try {
      await Promise.all([
        fetchSupplierData(),
        fetchProducts(),
        fetchOrders(),
        fetchAnalytics(),
      ]);
      setLastRefreshed(new Date());
      logger.info('✅ Dashboard data loaded successfully');
    } catch (error) {
      logger.error('Error loading dashboard data:', error);
      setLoadingError(error.message);
      setSnackbar({
        open: true,
        message: 'Error loading dashboard data. Please refresh.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      logger.info('Loading complete');
    }
  }, [fetchSupplierData, fetchProducts, fetchOrders, fetchAnalytics]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (!token || userType !== 'supplier') {
      logger.warn('Unauthorized, redirecting to login');
      navigate('/RawAuth');
      return;
    }
    loadDashboardData();
  }, [loadDashboardData, navigate, userType]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setChangePasswordDialog(true);
    setProfileAnchorEl(null);
  }, []);

  const handleRefresh = useCallback(() => {
    loadDashboardData();
    setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'info' });
  }, [loadDashboardData]);

  const handleViewProduct = useCallback((product) => {
    setSelectedProduct(product);
    setViewProductDialog(true);
  }, []);

  const handleViewOrder = useCallback((order) => {
    setSelectedOrder(order);
    setOrderDetailsDialog(true);
  }, []);

  // ============================================
  // ORDER STATUS UPDATE HANDLER
  // ============================================

  const handleUpdateOrderStatus = useCallback(async (orderId, newStatus) => {
    if (!orderId) {
      setSnackbar({ 
        open: true, 
        message: 'Invalid order ID', 
        severity: 'error' 
      });
      return;
    }

    if (!newStatus) {
      setSnackbar({ 
        open: true, 
        message: 'Please select a status', 
        severity: 'error' 
      });
      return;
    }

    setSnackbar({ 
      open: true, 
      message: `Updating order status to ${newStatus}...`, 
      severity: 'info' 
    });

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.ORDERS.UPDATE(orderId)}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: newStatus,
          user_id: userId,
          id: orderId
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, status: newStatus } 
              : order
          )
        );
        
        // Update order status data for charts
        setOrderStatusData(prev => 
          prev.map(item => {
            const oldStatus = orders.find(o => o.id === orderId)?.status;
            if (oldStatus && item.name.toLowerCase() === oldStatus) {
              return { ...item, value: Math.max(0, item.value - 1) };
            }
            if (item.name.toLowerCase() === newStatus) {
              return { ...item, value: item.value + 1 };
            }
            return item;
          })
        );
        
        setSnackbar({ 
          open: true, 
          message: `Order #${orderId} status updated to ${newStatus}`, 
          severity: 'success' 
        });
        
        setOrderDetailsDialog(false);
        fetchAnalytics();
        
      } else {
        throw new Error(result.message || 'Failed to update order status');
      }
    } catch (error) {
      logger.error('Error updating order status:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to update order status', 
        severity: 'error' 
      });
    }
  }, [orders, fetchAnalytics]);

  // ============================================
  // DOCUMENT UPLOAD HANDLERS (Dialog mode)
  // ============================================

  const handleDocumentUploadComplete = useCallback((docType, data) => {
    logger.info('Document uploaded successfully:', { docType, data });
    setDocumentUploadCount(prev => prev + 1);
    setDocumentUploadComplete(true);
    
    setSnackbar({
      open: true,
      message: `${docType} uploaded successfully!`,
      severity: 'success',
    });
    
    // Refresh supplier data to update verification status
    fetchSupplierData();
  }, [fetchSupplierData]);

  const handleDocumentUploadError = useCallback((error) => {
    logger.error('Document upload error:', error);
    setSnackbar({
      open: true,
      message: error || 'Failed to upload document',
      severity: 'error',
    });
  }, []);

  const handleDocumentUploadDialogOpen = useCallback(() => {
    setDocumentUploadDialogOpen(true);
  }, []);

  const handleDocumentUploadDialogClose = useCallback(() => {
    setDocumentUploadDialogOpen(false);
  }, []);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================

  const renderStats = () => {
    const statCards = [
      {
        icon: <StoreIcon />,
        title: 'Total Products',
        value: stats.totalProducts,
        subtitle: `${stats.activeProducts} active · ${stats.totalStock} in stock`,
        color: BRAND_COLORS.primary,
        trend: 'up',
        trendValue: '+12% from last month',
        progress: (stats.activeProducts / (stats.totalProducts || 1)) * 100,
      },
      {
        icon: <ShoppingBagIcon />,
        title: 'Total Requests',
        value: stats.totalOrders,
        subtitle: `${stats.pendingOrders} pending · ${stats.totalOrders - stats.pendingOrders} completed`,
        color: BRAND_COLORS.success,
        trend: growthData.orders_growth > 0 ? 'up' : growthData.orders_growth < 0 ? 'down' : null,
        trendValue: growthData.orders_growth !== 0 ? `${growthData.orders_growth.toFixed(1)}% from last month` : 'No change',
        progress: ((stats.totalOrders - stats.pendingOrders) / (stats.totalOrders || 1)) * 100,
      },
      {
        icon: `₦`,
        title: 'Total Revenue',
        value: formatCurrency(stats.totalRevenue),
        subtitle: `₦${(stats.totalRevenue / (stats.totalOrders || 1)).toFixed(2)} avg per order`,
        color: BRAND_COLORS.accent,
        trend: growthData.revenue_growth > 0 ? 'up' : growthData.revenue_growth < 0 ? 'down' : null,
        trendValue: growthData.revenue_growth !== 0 ? `${growthData.revenue_growth.toFixed(1)}% from last month` : 'No change',
      },
      {
        icon: <StarIcon />,
        title: 'Average Rating',
        value: stats.averageRating.toFixed(1),
        subtitle: `${products.filter(p => p.total_reviews > 0).length} products with reviews`,
        color: BRAND_COLORS.warning,
        progress: (stats.averageRating / 5) * 100,
      },
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
            <GlossyGradientCard {...card} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCharts = () => {
    const totalRevenue = monthlyRevenue.reduce((sum, item) => sum + item.revenue, 0);
    const totalOrders = monthlyRevenue.reduce((sum, item) => sum + item.orders, 0);

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card 
            sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Revenue Overview
                    <Chip 
                      label={`₦${totalRevenue.toLocaleString()}`} 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(BRAND_COLORS.primary, 0.1), 
                        color: BRAND_COLORS.primary,
                        fontWeight: 600,
                      }} 
                    />
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {totalOrders} orders across {monthlyRevenue.length} months
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label="Revenue" 
                    size="small" 
                    sx={{ bgcolor: alpha(BRAND_COLORS.primary, 0.1), color: BRAND_COLORS.primary }} 
                  />
                  <Chip 
                    label="Orders" 
                    size="small" 
                    sx={{ bgcolor: alpha(BRAND_COLORS.accent, 0.1), color: BRAND_COLORS.accent }} 
                  />
                </Box>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyRevenue}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BRAND_COLORS.primary} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={BRAND_COLORS.primary} stopOpacity={0.01}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#9e9e9e', fontSize: 11 }}
                    />
                    <YAxis 
                      yAxisId="left" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#9e9e9e', fontSize: 11 }}
                      tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#9e9e9e', fontSize: 11 }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                      }}
                      formatter={(value, name) => {
                        if (name === 'Revenue') return [`₦${value.toLocaleString()}`, name];
                        return [value, name];
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: 10 }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke={BRAND_COLORS.primary}
                      strokeWidth={3}
                      fill="url(#revenueGradient)"
                      name="Revenue"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="orders"
                      fill={BRAND_COLORS.accent}
                      radius={[4, 4, 0, 0]}
                      name="Orders"
                      barSize={30}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Distribution */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card 
            sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon sx={{ color: BRAND_COLORS.primary }} />
                Product Categories
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                {categoryDistribution.reduce((sum, cat) => sum + cat.value, 0)} total products
              </Typography>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || '#45B7D1'} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                      }}
                      formatter={(value, name) => [`${value} products`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 1 }}>
                {categoryDistribution.slice(0, 5).map((cat, i) => (
                  <Chip
                    key={i}
                    label={`${cat.name} (${cat.value})`}
                    size="small"
                    sx={{
                      bgcolor: alpha(cat.color || '#45B7D1', 0.1),
                      color: cat.color || '#45B7D1',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                ))}
                {categoryDistribution.length > 5 && (
                  <Chip
                    label={`+${categoryDistribution.length - 5} more`}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Status */}
        <Grid size={{ xs: 12 }}>
          <Card 
            sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon sx={{ color: BRAND_COLORS.primary }} />
                    Requests Status Distribution
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {orderStatusData.reduce((sum, item) => sum + item.value, 0)} total orders
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {orderStatusData.map((item) => (
                    <Chip
                      key={item.name}
                      label={`${item.name}: ${item.value}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(item.color, 0.1),
                        color: item.color,
                        fontWeight: 500,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
              <Box sx={{ height: 100 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={orderStatusData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                      }}
                      formatter={(value) => [`${value} orders`, '']}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || '#45B7D1'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card 
            sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: BRAND_COLORS.success }} />
                    Top Products
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Best performing products by revenue
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => navigate('/supplier/products')}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  View All
                </Button>
              </Box>
              <Box sx={{ maxHeight: 300, overflow: 'auto', pr: 0.5 }}>
                {topProducts.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No product data available yet
                    </Typography>
                  </Box>
                ) : (
                  topProducts.map((product, index) => (
                    <Fade in key={product.id} style={{ transitionDelay: `${index * 50}ms` }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          mb: 1,
                          borderRadius: 2,
                          border: '1px solid #f0f0f0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha(BRAND_COLORS.primary, 0.02),
                            borderColor: BRAND_COLORS.primary,
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                          },
                        }}
                        onClick={() => handleViewProduct(product)}
                      >
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            component="img"
                            src={getImageUrl(product.image) || 'default-product.png'}
                            alt={product.name}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              objectFit: 'cover',
                              bgcolor: '#f5f5f5',
                            }}
                            onError={(e) => {
                              e.target.src = 'default-product.png';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              bgcolor: BRAND_COLORS.primary,
                              color: 'white',
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          >
                            {index + 1}
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {product.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="textSecondary">
                              {formatCurrency(product.discountedPrice || product.price, product.currency)}
                            </Typography>
                            <Chip
                              label={`${product.total_orders || 0} orders`}
                              size="small"
                              sx={{ 
                                height: 20,
                                fontSize: '0.6rem',
                                bgcolor: alpha(BRAND_COLORS.info, 0.1),
                                color: BRAND_COLORS.info,
                              }}
                            />
                            {product.total_reviews > 0 && (
                              <Chip
                                label={`★ ${product.average_rating?.toFixed(1) || 0}`}
                                size="small"
                                sx={{ 
                                  height: 20,
                                  fontSize: '0.6rem',
                                  bgcolor: alpha(BRAND_COLORS.warning, 0.1),
                                  color: BRAND_COLORS.warning,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        <Typography variant="caption" fontWeight={600} color={BRAND_COLORS.primary}>
                          {formatCurrency(product.total_revenue || 0)}
                        </Typography>
                      </Box>
                    </Fade>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Orders */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card 
            sx={{ 
              borderRadius: 4, 
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon sx={{ color: BRAND_COLORS.accent }} />
                    Recent Requests
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Latest customers
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => navigate('/supplier/orders')}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  View All
                </Button>
              </Box>
              <Box sx={{ maxHeight: 300, overflow: 'auto', pr: 0.5 }}>
                {orders.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No orders yet
                    </Typography>
                  </Box>
                ) : (
                  orders.slice(0, 5).map((order, index) => {
                    const statusColor = getStatusColor(order.status);
                    return (
                      <Fade in key={order.id} style={{ transitionDelay: `${index * 50}ms` }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(BRAND_COLORS.primary, 0.02),
                              borderColor: BRAND_COLORS.primary,
                              transform: 'translateX(4px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                            },
                          }}
                          onClick={() => handleViewOrder(order)}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(statusColor, 0.12),
                              color: statusColor,
                              width: 40,
                              height: 40,
                              border: `2px solid ${alpha(statusColor, 0.15)}`,
                            }}
                          >
                            <ShoppingBagIcon sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {order.customer_name || `Order #${order.id}`}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" color="textSecondary">
                                {formatCurrency(order.total_amount || 0)}
                              </Typography>
                              <StatusChip
                                label={getStatusLabel(order.status)}
                                status={statusColor}
                                size="small"
                              />
                              {order.payment_status === 'paid' && (
                                <Chip
                                  label="Paid"
                                  size="small"
                                  sx={{ 
                                    height: 20,
                                    fontSize: '0.6rem',
                                    bgcolor: alpha(BRAND_COLORS.success, 0.1),
                                    color: BRAND_COLORS.success,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(order.order_date)}
                          </Typography>
                        </Box>
                      </Fade>
                    );
                  })
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // ============================================
  // RENDER DOCUMENT UPLOAD DIALOG
  // ============================================

  const renderDocumentUploadDialog = () => {
    return (
      <Dialog
        open={documentUploadDialogOpen}
        onClose={handleDocumentUploadDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxHeight: '90vh',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          },
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${alpha(BRAND_COLORS.primary, 0.08)}`,
            pb: 2,
            pt: 3,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                color: BRAND_COLORS.primary,
                width: 48,
                height: 48,
              }}
            >
              <CloudUploadIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Upload Business Documents
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Upload required documents for business verification
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDocumentUploadDialogClose}
            sx={{
              bgcolor: alpha(BRAND_COLORS.error, 0.05),
              color: BRAND_COLORS.error,
              '&:hover': {
                bgcolor: alpha(BRAND_COLORS.error, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflow: 'auto' }}>
          {/* Verification Status Banner */}
          {documentUploadComplete && supplierData?.business_verified === 1 && (
            <Alert 
              severity="success" 
              icon={<VerifiedUserIcon />}
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                border: `1px solid ${alpha(BRAND_COLORS.success, 0.2)}`,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Business Verified ✓
              </AlertTitle>
              <Typography variant="body2">
                Your business has been verified. You can now enjoy full platform access.
              </Typography>
            </Alert>
          )}

          {documentUploadComplete && supplierData?.business_verified === 0 && (
            <Alert 
              severity="info" 
              icon={<ScheduleIcon />}
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                border: `1px solid ${alpha(BRAND_COLORS.info, 0.2)}`,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Documents Under Review
              </AlertTitle>
              <Typography variant="body2">
                Your documents have been uploaded and are being reviewed. 
                You'll receive a notification once verification is complete.
              </Typography>
            </Alert>
          )}

          {/* Document Upload Component */}
          <DocumentUpload
            userId={userData?.id}
            userType="supplier"
            onUploadComplete={handleDocumentUploadComplete}
            onError={handleDocumentUploadError}
            onDocumentChange={(docs) => {
              logger.debug('Documents changed:', docs);
              // Update verification status if all required docs are uploaded
              const requiredTypes = ['cac_certificate'];
              const uploadedTypes = docs.filter(d => d.status === 'uploaded' || d.status === 'verified');
              const allUploaded = requiredTypes.every(type => 
                uploadedTypes.some(d => d.document_type === type)
              );
              if (allUploaded) {
                setDocumentUploadComplete(true);
              }
            }}
            sx={{
              '& .MuiCard-root': {
                borderRadius: 3,
                border: `1px solid ${alpha(BRAND_COLORS.primary, 0.1)}`,
                '&:hover': {
                  borderColor: alpha(BRAND_COLORS.primary, 0.3),
                },
              },
            }}
          />

          {/* Upload Progress/Status Summary */}
          {documentUploadCount > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: alpha(BRAND_COLORS.success, 0.05), borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: BRAND_COLORS.success, fontSize: 18 }} />
                {documentUploadCount} document{documentUploadCount > 1 ? 's' : ''} uploaded successfully
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Files are being processed. You can close this dialog and continue using the dashboard.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            borderTop: `1px solid ${alpha(BRAND_COLORS.primary, 0.08)}`,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleDocumentUploadDialogClose}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleDocumentUploadDialogClose();
              // Trigger refresh to update verification status
              fetchSupplierData();
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.accent})`,
              '&:hover': {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}dd, ${BRAND_COLORS.accent}dd)`,
              },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDashboard = () => {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Welcome, {supplierData?.company_name || userData?.display_name || 'Supplier'}
                {supplierData?.business_verified === 1 && (
                  <Tooltip title="Verified Business">
                    <VerifiedIcon sx={{ color: BRAND_COLORS.success, fontSize: 28 }} />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }}>
                Here's your account performance overview
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleDocumentUploadDialogOpen}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  borderColor: documentUploadComplete ? BRAND_COLORS.success : BRAND_COLORS.primary,
                  color: documentUploadComplete ? BRAND_COLORS.success : BRAND_COLORS.primary,
                  '&:hover': {
                    borderColor: documentUploadComplete ? BRAND_COLORS.success : BRAND_COLORS.primaryDark,
                    bgcolor: documentUploadComplete ? alpha(BRAND_COLORS.success, 0.04) : alpha(BRAND_COLORS.primary, 0.04),
                  },
                }}
              >
                Upload Documents
                {documentUploadComplete && (
                  <Badge 
                    badgeContent="✓" 
                    color="success" 
                    sx={{ ml: 1, '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                  />
                )}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/supplier/ProductRegistration')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.accent})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${BRAND_COLORS.primary}dd, ${BRAND_COLORS.accent}dd)`,
                  },
                }}
              >
                Add Product
              </Button>
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Last updated: {lastRefreshed.toLocaleString()}
          </Typography>
        </Box>

        {renderStats()}
        {renderCharts()}
      </Box>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  if (loading && products.length === 0 && orders.length === 0 && !loadingError) {
    return <Preloader text="Loading Dashboard..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F0F2F5' }}>
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isMobile={isMobile}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        userData={userData}
        wishlistCount={0}
        cartItemCount={0}
        onLogout={handleLogout}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {renderDashboard()}
        </Box>

        <Footer />
      </Box>

      {/* Document Upload Dialog */}
      {renderDocumentUploadDialog()}

      {/* View Product Dialog */}
      <Dialog
        open={viewProductDialog}
        onClose={() => setViewProductDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedProduct && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedProduct.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    CAS: {selectedProduct.registryNumber || 'N/A'}
                  </Typography>
                </Box>
                <IconButton onClick={() => setViewProductDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    component="img"
                    src={getImageUrl(selectedProduct.image) || 'default-product.png'}
                    alt={selectedProduct.name}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid #f0f0f0',
                    }}
                    onError={(e) => {
                      e.target.src = 'default-product.png';
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedProduct.description || 'No description available'}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Product Type
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedProduct.productType || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Price
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary">
                        {formatCurrency(selectedProduct.discountedPrice || selectedProduct.price, selectedProduct.currency)}
                        {selectedProduct.discounts > 0 && (
                          <Typography component="span" variant="caption" sx={{ textDecoration: 'line-through', ml: 1, color: 'text.secondary' }}>
                            {formatCurrency(selectedProduct.price, selectedProduct.currency)}
                          </Typography>
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Status
                      </Typography>
                      <StatusChip
                        label={getStatusLabel(selectedProduct.status)}
                        status={getStatusColor(selectedProduct.status)}
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        In Stock
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedProduct.quantity_in_stock || 0} units
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Rating
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedProduct.average_rating > 0 ? (
                          <>
                            ★ {selectedProduct.average_rating.toFixed(1)}
                            <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                              ({selectedProduct.total_reviews || 0} reviews)
                            </Typography>
                          </>
                        ) : 'No reviews yet'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewProductDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* View Order Dialog */}
      <Dialog
        open={orderDetailsDialog}
        onClose={() => setOrderDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Order #{selectedOrder.id}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(selectedOrder.order_date)}
                  </Typography>
                </Box>
                <IconButton onClick={() => setOrderDetailsDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Customer
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedOrder.customer_name || selectedOrder.buyer_name || 'N/A'}
                  </Typography>
                  {selectedOrder.customer_organisation && (
                    <Typography variant="caption" color="textSecondary">
                      {selectedOrder.customer_organisation}
                    </Typography>
                  )}
                </Grid>
                
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Amount
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    {formatCurrency(selectedOrder.total_amount || 0)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Current Status
                  </Typography>
                  <StatusChip
                    label={getStatusLabel(selectedOrder.status)}
                    status={getStatusColor(selectedOrder.status)}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Payment Status
                  </Typography>
                  <StatusChip
                    label={selectedOrder.payment_status || 'N/A'}
                    status={
                      selectedOrder.payment_status === 'paid' 
                        ? BRAND_COLORS.success 
                        : selectedOrder.payment_status === 'failed' 
                          ? BRAND_COLORS.error 
                          : BRAND_COLORS.warning
                    }
                    size="small"
                  />
                </Grid>
                
                {selectedOrder.intended_use && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Intended Use
                    </Typography>
                    <Typography variant="body2">
                      {selectedOrder.intended_use}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon sx={{ fontSize: 18 }} />
                Update Order Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
                  const isActive = selectedOrder.status === status;
                  const statusColor = getStatusColor(status);
                  return (
                    <Button
                      key={status}
                      size="small"
                      variant={isActive ? 'contained' : 'outlined'}
                      onClick={() => {
                        if (isActive) {
                          setSnackbar({ 
                            open: true, 
                            message: `Order is already ${status}`, 
                            severity: 'info' 
                          });
                          return;
                        }
                        handleUpdateOrderStatus(selectedOrder.id, status);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        minWidth: 80,
                        ...(isActive && {
                          bgcolor: statusColor,
                          color: '#fff',
                          '&:hover': { 
                            bgcolor: statusColor,
                            opacity: 0.9,
                          },
                          borderColor: statusColor,
                        }),
                        ...(!isActive && {
                          borderColor: statusColor,
                          color: statusColor,
                          '&:hover': {
                            bgcolor: alpha(statusColor, 0.05),
                            borderColor: statusColor,
                          },
                        }),
                      }}
                    >
                      {isActive && <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />}
                      {getStatusLabel(status)}
                    </Button>
                  );
                })}
              </Box>
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Click a status to update the order. Current: <strong>{getStatusLabel(selectedOrder.status)}</strong>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOrderDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={changePasswordDialog}
        onClose={() => setChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockOutlinedIcon color="primary" />
            Change Password
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Current Password" type="password" fullWidth size="small" />
            <TextField label="New Password" type="password" fullWidth size="small" />
            <TextField label="Confirm New Password" type="password" fullWidth size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            sx={{ 
              bgcolor: BRAND_COLORS.primary,
              '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
            }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            '& .MuiAlert-icon': { alignItems: 'center' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles for animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          ::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          ::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>
    </Box>
  );
};

export default SupplierDashboard;